'use client'

import React, { useEffect, useState } from 'react'
import {
  Button,
  Col,
  Icon,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  Row,
  Typography,
} from '../index'
import './pdf-reader.css'
import type { PdfReaderProps } from './pdf-reader.types'

// Lazy load react-pdf to avoid SSR issues
type ReactPdfDocumentProps = {
  file: string | null
  onLoadSuccess: (data: { numPages: number }) => void
  onLoadError: (error: Error) => void
  loading?: React.ReactNode
  className?: string
  children?: React.ReactNode
}

type ReactPdfPageProps = {
  pageNumber: number
  renderTextLayer?: boolean
  renderAnnotationLayer?: boolean
  width?: number
  onLoadSuccess?: (page: { height: number; width: number }) => void
  className?: string
}

type ReactPdfModule = {
  Document: React.ComponentType<ReactPdfDocumentProps>
  Page: React.ComponentType<ReactPdfPageProps>
  pdfjs: {
    GlobalWorkerOptions: {
      workerSrc: string
    }
  }
}

let reactPdfModule: ReactPdfModule | null = null
let isReactPdfLoading = false

const loadReactPdf = async (): Promise<ReactPdfModule> => {
  if (reactPdfModule) return reactPdfModule
  if (isReactPdfLoading) {
    // Wait for existing load
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (reactPdfModule) {
          clearInterval(checkInterval)
          resolve(reactPdfModule)
        }
      }, 50)
    })
  }

  isReactPdfLoading = true
  try {
    reactPdfModule = await import('react-pdf')
    if (typeof window !== 'undefined' && reactPdfModule.pdfjs) {
      reactPdfModule.pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js'
    }
    isReactPdfLoading = false
    return reactPdfModule
  } catch (error) {
    isReactPdfLoading = false
    throw error
  }
}

export const PdfReader = ({
  open,
  onOpenChange,
  docURL,
  title,
  translations,
}: PdfReaderProps) => {
  const [numPages, setNumPages] = useState<number | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [error, setError] = useState<string | null>(null)
  const [pageWidth, setPageWidth] = useState(800)
  const [pageHeight, setPageHeight] = useState<number | null>(null)
  const [scale, setScale] = useState(1)
  const [isPdfModuleLoaded, setIsPdfModuleLoaded] = useState(false)
  const [PdfDocument, setPdfDocument] =
    useState<React.ComponentType<ReactPdfDocumentProps> | null>(null)
  const [PdfPage, setPdfPage] =
    useState<React.ComponentType<ReactPdfPageProps> | null>(null)

  const pdfUrl = docURL || null

  const t = {
    loading: translations?.loading || 'Loading PDF...',
    noUrl: translations?.noUrl || 'PDF URL is not available',
    close: translations?.close || 'Close',
    previous: translations?.previous || 'Previous',
    next: translations?.next || 'Next',
    page: translations?.page || 'Page',
    of: translations?.of || 'of',
  }

  useEffect(() => {
    const updateWidth = () => {
      if (typeof window !== 'undefined') {
        const isMobile = window.innerWidth <= 768
        setPageWidth(isMobile ? window.innerWidth - 64 : 800)
      }
    }

    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  // Load react-pdf module on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && open) {
      loadReactPdf()
        .then((module) => {
          setPdfDocument(() => module.Document)
          setPdfPage(() => module.Page)
          setIsPdfModuleLoaded(true)
        })
        .catch((err) => {
          setError(err.message || 'Failed to load PDF module')
        })
    }
  }, [open])

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setError(null)
    setPageNumber(1)
  }

  const onPageLoadSuccess = (page: { height: number; width: number }) => {
    setPageHeight(page.height * (pageWidth / page.width))
  }

  const onDocumentLoadError = (error: Error) => {
    setError(error.message || 'Error loading PDF')
  }

  const goToPrevPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1)
      setPageHeight(null)
    }
  }

  const goToNextPage = () => {
    if (numPages && pageNumber < numPages) {
      setPageNumber(pageNumber + 1)
      setPageHeight(null)
    }
  }

  const handleClose = () => {
    setPageNumber(1)
    setNumPages(null)
    setError(null)
    setScale(1)
    onOpenChange(false)
  }

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3))
  }

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5))
  }

  const resetZoom = () => {
    setScale(1)
  }

  return (
    <Modal open={open} onOpenChange={handleClose}>
      <ModalContent className='!fixed !indent-0 !w-screen !h-screen !max-w-full !max-h-full m-0 !rounded-none p-0 flex flex-col'>
        <ModalHeader className='flex-shrink-0 border-b border-neutral-surface-dark p-xl sm:p-sm'>
          <Row className='justify-between items-center gap-md'>
            <ModalTitle className='flex-1 text-center'>{title}</ModalTitle>
            <Row className='w-fit gap-2xs'>
              <Button
                variant='transparent'
                size='xs'
                onClick={zoomOut}
                disabled={scale <= 0.5}
                className='flex-shrink-0'
              >
                <Icon name='Minus' size='sm' />
              </Button>
              <Button
                variant='ghost'
                size='xs'
                onClick={resetZoom}
                className='flex-shrink-0 px-xs'
              >
                <Typography size='xs'>{Math.round(scale * 100)}%</Typography>
              </Button>
              <Button
                variant='transparent'
                size='xs'
                onClick={zoomIn}
                disabled={scale >= 3}
                className='flex-shrink-0'
              >
                <Icon name='Plus' size='sm' />
              </Button>
            </Row>
          </Row>
        </ModalHeader>

        <Col className='flex-1 overflow-hidden relative'>
          {!pdfUrl ? (
            <Col className='flex-1 items-center justify-center gap-md p-xl'>
              <Typography size='md' color='text-neutral-surface-80'>
                {t.noUrl}
              </Typography>
            </Col>
          ) : error ? (
            <Col className='flex-1 items-center justify-center gap-md p-xl'>
              <Icon name='AlertTriangle' size='2xl' />
              <Typography size='md' color='text-neutral-surface-80'>
                {error}
              </Typography>
              <Button variant='outline' onClick={handleClose}>
                {t.close}
              </Button>
            </Col>
          ) : (
            <>
              <Col
                className='flex-1 overflow-auto bg-neutral-surface-10'
                id='pdf-scroll-container'
              >
                <div
                  className='flex items-center justify-center transition-all ease-in-out duration-300'
                  style={{
                    minWidth: pageHeight ? `${pageWidth * scale}px` : '100%',
                    minHeight: pageHeight ? `${pageHeight * scale}px` : '100%',
                    padding: '2rem',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  {isPdfModuleLoaded && PdfDocument && PdfPage ? (
                    <PdfDocument
                      file={pdfUrl}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={onDocumentLoadError}
                      loading={
                        <Col className='items-center justify-center gap-md'>
                          <Typography size='md' color='text-neutral-surface-80'>
                            {t.loading}
                          </Typography>
                        </Col>
                      }
                      className='flex items-center justify-center'
                    >
                      <div
                        className='transition-all ease-in-out duration-300'
                        style={{
                          transform: `scale(${scale})`,
                          transformOrigin: 'center center',
                          display: 'inline-block',
                        }}
                      >
                        <PdfPage
                          pageNumber={pageNumber}
                          renderTextLayer={true}
                          renderAnnotationLayer={true}
                          width={pageWidth}
                          onLoadSuccess={onPageLoadSuccess}
                          className='shadow-lg'
                        />
                      </div>
                    </PdfDocument>
                  ) : (
                    <Col className='items-center justify-center gap-md'>
                      <Typography size='md' color='text-neutral-surface-80'>
                        {t.loading}
                      </Typography>
                    </Col>
                  )}
                </div>
              </Col>

              {numPages && numPages > 1 && (
                <Row className='flex-shrink-0 justify-between sm:justify-center items-center gap-md p-md border-t border-neural-surface-dark'>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={goToPrevPage}
                    disabled={pageNumber <= 1}
                  >
                    <Icon name='ChevronRight' size='sm' />
                    <Typography size='sm'>{t.previous}</Typography>
                  </Button>

                  <Typography size='sm' color='text-neutral-surface-80'>
                    {`${t.page} ${String(pageNumber)} ${t.of} ${String(numPages)}`}
                  </Typography>

                  <Button
                    variant='outline'
                    size='sm'
                    onClick={goToNextPage}
                    disabled={pageNumber >= numPages}
                  >
                    <Typography size='sm'>{t.next}</Typography>
                    <Icon name='ChevronLeft' size='sm' />
                  </Button>
                </Row>
              )}
            </>
          )}
        </Col>
      </ModalContent>
    </Modal>
  )
}
